/* eslint-disable react-hooks/exhaustive-deps */
import rateSvc from '@efex-services/rate/rateSvc';
import { useRole } from '@efexteam/permission-manager';
import {
  Alert,
  AlertTitle,
  Divider,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { selectProfileData } from '@pages/profile/store/selectors';
import DateService from '@utils/DateService';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { Button } from '@efexteam/components';
import DetailView from '../components/DetailView';
import PAYMENT_STEPS from '../constants';
import { useFormData } from '../context';

interface ScheduleProps {
  nextFormStep: () => void;
  prevFormStep: (value: number) => void;
}

type FormData = {
  dueDate: DateTime;
};

const Schedule = ({ nextFormStep, prevFormStep }: ScheduleProps) => {
  const { t, i18n } = useTranslation();
  const { isPaymentAnalyst } = useRole();
  const userProfile = useSelector(selectProfileData);
  const dateService = new DateService();

  const oneYearInTheFuture = new Date();
  oneYearInTheFuture.setFullYear(oneYearInTheFuture.getFullYear() + 1);

  const { setFormValues, data } = useFormData();
  const { isForward, amount: formAmount, reverse, rateData } = data;

  const {
    data: spreadData,
    loading,
    error,
    invoke: getForwardValues,
  } = useApiCall(
    ({ amount = '0', date = DateTime.now().toISODate(), enterpriseId }) =>
      rateSvc.getForwardValues({
        amount,
        reverse,
        rateUuid: rateData?.reference || '',
        date,
        enterpriseId,
      })
  );

  const {
    formState: { errors },
    control,
  } = useForm<FormData>();

  const updateDueDate = (date: DateTime) => {
    setFormValues({ ...data, dueDate: date });
    const enterpriseId = userProfile?.company?.enterpriseId;
    if (isForward && rateData?.reference && enterpriseId) {
      getForwardValues({
        amount: formAmount,
        date: date.toISODate(),
        enterpriseId,
      });
    }
  };

  const clearAndGoBack = () => {
    setFormValues({ ...data, dueDate: undefined });
    prevFormStep(PAYMENT_STEPS.PAYMENT_METHOD);
  };

  useEffect(() => {
    setFormValues({
      ...data,
      forwardValues: spreadData || undefined,
    });
  }, [spreadData]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t(
          isPaymentAnalyst ? 'scheduleConfigureTitle' : 'schedulePaymentTitle'
        )}
      </Typography>

      <Grid container>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {t(`choosePaymentDate`)}
            </Typography>
            <Controller
              name="dueDate"
              rules={{ required: true }}
              control={control}
              defaultValue={data?.dueDate || DateTime.now()}
              render={({ field }) => (
                <LocalizationProvider
                  dateAdapter={AdapterLuxon}
                  adapterLocale={i18n.language}
                >
                  <DatePicker
                    {...field}
                    disablePast
                    className={clsx('w-[90%]')}
                    minDate={dateService.addDaysToDate(
                      DateTime.now().toISODate(),
                      2
                    )}
                    shouldDisableDate={(date) =>
                      dateService.isDateDisabled(date)
                    }
                    maxDate={
                      isForward
                        ? DateTime.fromJSDate(oneYearInTheFuture)
                        : undefined
                    }
                    onChange={(value) => {
                      if (!value) return;
                      updateDueDate(value);
                    }}
                    slotProps={{
                      // eslint-disable-next-line react/no-unstable-nested-components
                      textField: (params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={!!errors.dueDate}
                          fullWidth
                        />
                      ),
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            {errors && errors.dueDate && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
          </FormControl>
        </Grid>
        {isForward ? (
          <Alert severity="info" className="w-full !mb-5 !mt-1">
            <AlertTitle>{t('forwardMessage')}</AlertTitle>
            <Typography variant="body2">1. {t('forwardMessage1')}</Typography>
            <Divider className="!my-2" />
            <Typography variant="body2">2. {t('forwardMessage2')}</Typography>
          </Alert>
        ) : null}
        {error ? (
          <Alert className="mb-4 w-full" severity="error">
            <AlertTitle>{t('errorRetryMessage')}</AlertTitle>
            {t((error as any).response.data.code ?? 1000)}
          </Alert>
        ) : null}
        <DetailView />
        {isForward && data.dueDate ? (
          <Alert className="mb-4 w-full" severity="warning">
            <AlertTitle>{t('forwardWarning2')}</AlertTitle>
            {t('forwardWarning', { date: data.dueDate.toLocaleString() })}
          </Alert>
        ) : null}
        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row gap-4"
        >
          <Button
            appearance="secondary"
            size="large"
            className="mt-4"
            onClick={clearAndGoBack}
          >
            {t('back')}
          </Button>
          <Button
            disabled={
              loading ||
              !!error ||
              (!data.dueDate && isForward ? !spreadData?.spread : false)
            }
            appearance="primary"
            size="large"
            className="mt-4"
            onClick={nextFormStep}
          >
            {t(isForward ? 'forwardButton' : 'schedulePayment')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default Schedule;
